import { nowUTC } from "@homebound/activesupport";
import {
  BoundMultiSelectField,
  Button,
  ButtonMenu,
  Checkbox,
  Css,
  Icon,
  type MenuItem,
  Palette,
  SelectField,
  useBreakpoint,
  useComputed,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { Fragment, useCallback, useMemo } from "react";
import { BoundAiTextField } from "src/components/formFields/ai";
import { useAiFieldProposalState } from "src/components/formFields/ai/utils/useAiFieldProposalState";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import { MaterialType } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { MaterialCatalog_ItemFragment } from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { MaterialVariantFormValue } from "src/routes/libraries/material-catalog/components/materialListingForm";
import {
  MergeHistoryModal,
  mergeHistoryModalOptions,
} from "src/routes/libraries/material-catalog/components/merge-history-modal/MergeHistoryModal";
import {
  TakeoffsUsageModal,
  takeoffsUsageModalOptions,
} from "src/routes/libraries/material-catalog/components/takeoffs-usage-modal/TakeoffsUsageModal";
import { VariantThumbnailStrip } from "src/routes/libraries/material-catalog/material-listing-page/components/VariantThumbnailStrip";
import { useMaterialListingContext } from "src/routes/libraries/material-catalog/material-listing-page/components/material-listing-context/MaterialListingContext";
import { ObjectState } from "src/utils/formState";
import { MaterialDimensionsForm } from "../../components/MaterialDimensionsForm";

export type VariantDetailsSectionProps = {
  items: MaterialCatalog_ItemFragment[];
  tags: NamedFragment[];
  /**
   * Allow the AI Import to replace the variant's three-dot menu, rather than adding to it.
   */
  menuItems?: MenuItem[];
};

/**
 * The selected variant: thumbnail strip, then its fields.
 *
 * Contains the static variant fields (i.e. sku, status, website, etc.)
 * and the dynamic attribute fields (i.e. color, finish, etc) that are taken from the item's MADs.
 */
export function VariantDetailsSection(props: VariantDetailsSectionProps) {
  const { items, tags, menuItems: menuItemsOverride } = props;
  const {
    formState,
    selectedVariantForm: variant,
    addVariant,
    copyVariant,
    variants,
    isEditingPendingVariant,
  } = useMaterialListingContext();
  const { openModal } = useModal();
  const tid = useTestIds({}, "variantDetails");
  const { sm } = useBreakpoint();

  const type = useComputed(() => formState.type.value, [formState]);
  const deletedAt = useComputed(() => variant?.deletedAt.value, [variant]);
  const variantId = useComputed(() => variant?.id.value, [variant]);
  const canEdit = useComputed(() => variant?.canEdit.value, [variant]);
  const hasPendingVariant = useComputed(() => formState.variants.rows.some((v) => !v.id.value), [formState]);
  const website = useComputed(() => {
    const url = variant?.manufacturerUrl.value;
    if (!url) return undefined;
    return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
  }, [variant]);
  // Numbered in strip order (default first) so "Variant 1" is the leading thumbnail, not form-row order.
  const variantNumber = useComputed(() => {
    if (!variant) return undefined;
    if (isEditingPendingVariant || !variant.id.value) return variants.length + 1;
    const index = variants.findIndex((v) => v.id === variant.id.value);
    return index === -1 ? variants.length + 1 : index + 1;
  }, [variant, variants, isEditingPendingVariant]);
  const isDefault = useComputed(
    () => !!variantId && formState.defaultVariantId.value === variantId,
    [formState, variantId],
  );
  // Server-assigned, so pending variants have none until they are saved.
  const code = useComputed(() => variant?.code.value, [variant]);

  const skuAfpState = useAiFieldProposalState(variant?.fieldProposals, "modelNumber");
  const websiteAfpState = useAiFieldProposalState(variant?.fieldProposals, "manufacturerUrl");

  // Confirm before discarding an in-progress pending row when copying a saved variant over it.
  const handleMakeACopy = useCallback(() => {
    if (hasPendingVariant && variantId) {
      openModal({
        content: (
          <ConfirmationModal
            title="Replace unsaved variant?"
            confirmationMessage="You already have an unsaved variant in progress. Making a copy will discard it and replace it with a copy of this variant."
            label="Continue"
            danger
            onConfirmAction={copyVariant}
          />
        ),
      });
      return;
    }
    copyVariant();
  }, [hasPendingVariant, variantId, openModal, copyVariant]);

  // Menu is saved-variant only — new/pending rows have no id for lookups or a meaningful copy source.
  const menuItems = useMemo(
    () =>
      variantId
        ? (menuItemsOverride ?? [
            { label: "Make a copy", onClick: handleMakeACopy },
            {
              label: "Usage Details",
              onClick: () =>
                openModal({
                  content: <TakeoffsUsageModal materialVariantId={variantId} />,
                  ...takeoffsUsageModalOptions,
                }),
            },
            {
              label: "Merge History",
              onClick: () =>
                openModal({
                  content: <MergeHistoryModal materialVariantId={variantId} />,
                  ...mergeHistoryModalOptions,
                }),
            },
          ])
        : [],
    [handleMakeACopy, openModal, variantId, menuItemsOverride],
  );
  const itemId = useComputed(() => formState.itemId.value, [formState]);
  const item = useMemo(() => items.find((i) => i.id === itemId), [items, itemId]);
  const duplicateError = useComputed(
    () => (variant ? duplicateVariantError(formState.variants.rows, variant) : undefined),
    [formState, variant],
  );

  if (!variant) return null;

  const isPlaceholder = type === MaterialType.Placeholder;

  return (
    <div {...tid} css={Css.relative.df.fdc.gap3.w100.$}>
      {/* 
          FormPageLayout section headers don't currently support actions.
          This is hacky for now, but we are going to move this form to the `FocusedFormLayout` component soon.
          So leaving this here for now.
          TODO: Move this to something more beam native on the `FocusedFormLayout` component.
          */}
      <div css={Css.absolute.right0.add("top", "calc(-34px - var(--t-spacing) * 3)").$}>
        <Button label="Add Variant" variant="secondary" icon="plus" onClick={addVariant} />
      </div>
      <div css={Css.df.aic.jcsb.hPx(40).$}>
        <div css={Css.df.aic.gap2.$}>
          <span css={Css.lg.$} {...tid.heading}>
            Variant {variantNumber}
          </span>
          {code && (
            <span css={Css.md.gray700.$} {...tid.code}>
              {code}
            </span>
          )}
        </div>
        {menuItems.nonEmpty && (
          <ButtonMenu {...tid.menu} trigger={{ icon: "verticalDots", variant: "outline" }} items={menuItems} />
        )}
      </div>

      <VariantThumbnailStrip />

      {duplicateError && (
        <span css={Css.red600.$} {...tid.duplicateError}>
          {duplicateError} please <b>modify</b> or remove this variant
        </span>
      )}

      {/* Remount on variant switch so `useAiProposal`'s per-input "has typed" state does not leak into other variants.*/}
      <Fragment key={variantId ?? "pending"}>
        <div
          css={
            // On smaller screens, just use 1 column
            Css.dg.gtc(sm ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)").gap3.w100.$
          }
        >
          {/* Placeholders have no SKU — they stand in for a product that isn't chosen yet. */}
          {!isPlaceholder && (
            <BoundAiTextField
              label="SKU"
              field={variant.modelNumber}
              aiProposal={skuAfpState}
              disabled={canEdit && disableBasedOnPotentialOperation(canEdit)}
            />
          )}
          <BoundMultiSelectField
            label="Tags"
            field={variant.tags}
            options={tags}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.id}
          />
          {/* Archiving is a soft delete, so the control is a status rather than a delete button. */}
          <SelectField
            label="Status"
            value={deletedAt ? "archive" : "active"}
            options={[
              { id: "active", name: "Active" },
              { id: "archive", name: "Archive" },
            ]}
            onSelect={(status) => variant.deletedAt.set(status === "archive" ? nowUTC() : null)}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.id}
          />
          {!isPlaceholder && (
            <BoundAiTextField
              label="Website"
              field={variant.manufacturerUrl}
              aiProposal={websiteAfpState}
              endAdornment={
                website && (
                  <a href={website} target="_blank" rel="noreferrer">
                    <Icon icon="linkExternal" color={Palette.Gray700} />
                  </a>
                )
              }
            />
          )}
        </div>

        <MaterialDimensionsForm
          type={type}
          variant={variant}
          dimensions={item?.materialAttributeDimensions ?? []}
          showColumns
          aiProposals={variant.fieldProposals}
        />
      </Fragment>

      {/* The pointer lives on the *listing*, so checking this is "make this variant the default", and there is
          nothing to uncheck to — a listing always has one. Hence checked-and-disabled rather than a toggle. */}
      <Checkbox
        label="Set as Default"
        selected={isDefault}
        disabled={
          isDefault
            ? "This is already the default variant. Set a different one as default to change it."
            : !variantId
              ? "Save this variant before making it the default."
              : deletedAt
                ? "An archived variant cannot be the default."
                : undefined
        }
        onChange={() => formState.defaultVariantId.set(variantId)}
      />
    </div>
  );
}

/** Same SKU + attribute set as another variant on this listing — the backend will reject the save. */
function duplicateVariantError(
  rows: readonly ObjectState<MaterialVariantFormValue>[],
  variant: ObjectState<MaterialVariantFormValue>,
): string | undefined {
  // Group variants by their MAV IDs + SKU
  const groups = rows.groupBy((v) => {
    const mavKey = [...(v.mavIds.value ?? [])].sort().join(",");
    return `${v.modelNumber.value}-${mavKey}`;
  });
  const dups = groups.toEntries().find(([, vs]) => vs.includes(variant) && vs.length > 1)?.[1];
  if (!dups) return undefined;
  const other = dups.find((v) => v !== variant);
  const otherLabel = other?.code.value || other?.modelNumber.value || "the new variant";
  return `This is the same as “${otherLabel}”`;
}
